/* eslint-disable @typescript-eslint/no-unused-vars */
import { createClient, SupabaseClient, RealtimeChannel } from "@supabase/supabase-js";

// هذه المتغيرات يجب أن تكون في ملف .env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "https://srrsrfwognluuaxudjhi.supabase.co";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

console.log("Supabase configuration:", { 
  url: supabaseUrl,
  keyLength: supabaseAnonKey ? supabaseAnonKey.length : 0,
  hasKey: !!supabaseAnonKey
});

// Supabase configuration validation

// التحقق من وجود متغيرات البيئة المطلوبة
if (!supabaseUrl || !supabaseAnonKey) {
  // Environment variables for Supabase are not set correctly
  console.error("Supabase URL or Anon Key is missing");
}

// Initialize Supabase client

// إنشاء عميل Supabase الحقيقي مع إعدادات محسنة
let supabase: SupabaseClient;

try {
  // إنشاء عميل مع خيارات محددة لتحسين الأداء والاتصال
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true, // Changed to true to detect reset password tokens in URL
      flowType: "pkce", // Use PKCE flow for more secure authentication
      storageKey: 'sb-auth-token', // Use a consistent key for auth storage
      storage: {
        getItem: (key) => {
          // Get from cookies only
          const cookieValue = document.cookie
            .split('; ')
            .find(row => row.startsWith(`${key}=`))
            ?.split('=')[1];
          
          return cookieValue ? decodeURIComponent(cookieValue) : null;
        },
        setItem: (key, value) => {
          // Set in cookie with secure flags
          const expires = new Date();
          expires.setDate(expires.getDate() + 7); // 7 days
          
          // Check if we're on HTTPS or localhost for secure flag
          const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
          const secureFlag = isSecure ? '; secure' : '';
          
          document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; samesite=strict${secureFlag}`;
          
          // Also update isLoggedIn flag when setting auth token
          if (key.startsWith('sb-') && value) {
            document.cookie = `isLoggedIn=true; expires=${expires.toUTCString()}; path=/; samesite=strict${secureFlag}`;
          }
          
          // Clean up localStorage if it exists (one-time cleanup)
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
          }
        },
        removeItem: (key) => {
          // Remove from cookies
          document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          
          // Update isLoggedIn status if removing auth token
          if (key.startsWith('sb-')) {
            document.cookie = 'isLoggedIn=false; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          }
          
          // Clean up localStorage if it exists
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
          }
        }
      }
    },
    global: {
      headers: {
        'x-application-name': 'dashcode-vite',
      },
      fetch: (...args) => fetch(...args),
    },
    // Setting realtime parameters to reduce connection issues
    realtime: {
      params: {
        eventsPerSecond: 1,
      },
    },
  });
  
} catch (error) {
  // Create a minimal client as fallback
  supabase = createClient(supabaseUrl, supabaseAnonKey);
  console.error("Error creating Supabase client with custom options, using default client", error);
}

// Cache for realtime channels to avoid creating multiple channels for the same purpose
const channelCache: Record<string, RealtimeChannel> = {};

// Global broadcast channel for messaging
let globalMessagingChannel: RealtimeChannel | null = null;

// Initialize the global messaging channel
export const getGlobalMessagingChannel = (): RealtimeChannel => {
  if (globalMessagingChannel) {
    return globalMessagingChannel;
  }
  
  globalMessagingChannel = supabase.channel('global-messaging', {
    config: {
      broadcast: { self: true }
    }
  });
  
  // Subscribe to the channel
  globalMessagingChannel.subscribe((status) => {
    console.log(`Global messaging channel status: ${status}`);
    
    if (status === 'CHANNEL_ERROR') {
      console.error('Error connecting to global messaging channel');
      globalMessagingChannel = null;
    }
  });
  
  return globalMessagingChannel;
};

// simpler implementation without realtime
export const setupMessagingRealtime = (channelName = 'messaging-changes'): RealtimeChannel => {
  try {
    // Return a dummy channel that doesn't actually do anything
    // This will be used for typechecking but we'll use polling instead of realtime
    console.log(`Setting up dummy channel: ${channelName} (realtime disabled)`);
    
    // Just create a placeholder that satisfies the interface but doesn't connect
    const dummyChannel = {
      // Basic methods needed to satisfy the interface
      subscribe: (callback?: (status: string) => void) => {
        if (callback) callback('SUBSCRIBED');
        return dummyChannel;
      },
      unsubscribe: () => Promise.resolve({}),
      on: (event: string, filter?: Record<string, unknown>, callback?: (payload: unknown) => void) => {
        // Store callbacks but don't actually connect to realtime
        console.log(`Registered listener for ${event} but realtime is disabled`);
        return dummyChannel;
      },
      // Add any other methods needed by the RealtimeChannel interface
      send: (payload: Record<string, unknown>) => Promise.resolve({})
    } as unknown as RealtimeChannel;
    
    return dummyChannel;
  } catch (error) {
    console.error("Error setting up messaging:", error);
    // Return dummy channel
    return {
      unsubscribe: () => Promise.resolve(),
      on: () => ({ on: () => ({ subscribe: () => ({}) }) }),
      subscribe: () => ({}),
    } as unknown as RealtimeChannel;
  }
};

// التحقق من أن عملية الاتصال تمت بنجاح باستخدام جدول courses بدلاً من pg_tables
const testConnection = async () => {
  try {
    const result = await supabase.from('courses').select('id').limit(1);
    
    if (result.error) {
      console.warn("Supabase connection test failed:", result.error.message);
      return false;
    }
    
    console.log("Supabase connection successful");
    return true;
  } catch (e) {
    console.error("Error testing Supabase connection:", e);
    return false;
  }
};

// تنفيذ الاختبار ولكن عدم انتظار النتيجة لتجنب تعطيل التطبيق
testConnection();

// Test realtime connection specifically
const testRealtimeConnection = async () => {
  try {
    console.log("Testing realtime connection...");
    
    // Create a test channel with minimal configuration
    const testChannel = supabase.channel('test-realtime-connection', {
      config: {
        broadcast: { self: true },
      }
    });
    
    // Subscribe to the channel and log status
    testChannel.subscribe((status) => {
      console.log(`Realtime test connection status: ${status}`);
      
      if (status === 'CHANNEL_ERROR') {
        console.error("Realtime connection test failed");
        console.log("Debug info:");
        console.log("- URL:", supabaseUrl);
        console.log("- Browser:", navigator.userAgent);
        console.log("- Connection: Connection info not available");
        console.log("- Headers:", {
          'x-application-name': 'dashcode-vite',
        });
      } else if (status === 'SUBSCRIBED') {
        console.log("Realtime connection test successful");
        
        // Remove the test channel after successful connection
        setTimeout(() => {
          supabase.removeChannel(testChannel);
        }, 2000);
      }
    });
    
    return true;
  } catch (e) {
    console.error("Error testing realtime connection:", e);
    return false;
  }
};

// Run the realtime connection test
testRealtimeConnection();

// تصدير عميل Supabase للاستخدام في بقية التطبيق
export default supabase;
